import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import Header from "@/components/Header";
import { Image, Album } from "@/lib/types";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck } from "lucide-react";
import FloatingButton from "@/components/FloatingButton";
import AdminChat from "@/components/AdminChat";

// Import dashboard components
import ImagesTab from "@/components/dashboard/ImagesTab";
import AlbumsTab from "@/components/dashboard/AlbumsTab";
import AdminPanel from "@/components/dashboard/AdminPanel";
import Stats from "@/components/Stats";
import SiteSettings from "@/components/SiteSettings";

const Dashboard = () => {
  const [images, setImages] = useState<Image[]>([]);
  const [albums, setAlbums] = useState<Album[]>([]);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [selectedAlbum, setSelectedAlbum] = useState<Album | null>(null);
  const [loadingImages, setLoadingImages] = useState(true);
  const [loadingAlbums, setLoadingAlbums] = useState(true);
  const [addToAlbumOpen, setAddToAlbumOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const { session, user, isAdmin } = useAuth();
  const navigate = useNavigate();

  // Check if user is logged in
  useEffect(() => {
    if (!session?.user) {
      navigate("/auth");
    }
  }, [session, navigate]);

  // Fetch user's images
  useEffect(() => {
    if (!session?.user) return;

    const fetchImages = async () => {
      setLoadingImages(true);
      try {
        const { data, error } = await supabase
          .from('images')
          .select('*')
          .eq('user_id', session.user.id)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching images:', error);
          toast.error('Failed to load your images');
          setImages([]);
          return;
        }

        if (!data || data.length === 0) {
          setImages([]);
          setLoadingImages(false);
          return;
        }

        // Map database results to our Image type
        const fetchedImages = await Promise.all(data.map(async (img) => {
          const { data: { publicUrl } } = supabase.storage
            .from('images')
            .getPublicUrl(img.storage_path);

          // Try to determine contentType from storage path
          let contentType: string | undefined = undefined;
          const extension = img.storage_path.split('.').pop()?.toLowerCase();
          
          if (extension) {
            if (['mp4', 'webm', 'mov'].includes(extension)) {
              contentType = `video/${extension === 'mov' ? 'quicktime' : extension}`;
            } else if (extension === 'gif') {
              contentType = 'image/gif';
            } else if (['jpg', 'jpeg', 'png', 'webp'].includes(extension)) {
              contentType = `image/${extension === 'jpg' ? 'jpeg' : extension}`;
            }
          }

          return {
            id: img.id,
            name: img.name,
            description: img.description || undefined,
            url: publicUrl,
            slug: img.slug,
            createdAt: new Date(img.created_at).getTime(),
            views: img.views,
            contentType
          };
        }));

        setImages(fetchedImages);
      } catch (err) {
        console.error('Error processing images:', err);
        toast.error('Error loading images');
      } finally {
        setLoadingImages(false);
      }
    };

    fetchImages();
  }, [session]);

  // Fetch user's albums
  useEffect(() => {
    if (!session?.user) return;

    const fetchAlbums = async () => {
      setLoadingAlbums(true);
      try {
        const { data, error } = await supabase
          .from('albums')
          .select('*')
          .eq('user_id', session.user.id)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching albums:', error);
          toast.error('Failed to load your albums');
          setAlbums([]);
          return;
        }

        if (!data || data.length === 0) {
          setAlbums([]);
          setLoadingAlbums(false);
          return;
        }

        // Map database results to our Album type
        const fetchedAlbums = await Promise.all(data.map(async (album) => {
          // Get album images for preview
          const { data: albumImages } = await supabase
            .from('album_images')
            .select('image_id')
            .eq('album_id', album.id)
            .limit(4);
          
          let albumImagesData: Image[] = [];
          
          if (albumImages && albumImages.length > 0) {
            const imageIds = albumImages.map(item => item.image_id);
            const { data: imagesData } = await supabase
              .from('images')
              .select('*')
              .in('id', imageIds);
            
            if (imagesData && imagesData.length > 0) {
              albumImagesData = await Promise.all(imagesData.map(async (img) => {
                const { data: { publicUrl } } = supabase.storage
                  .from('images')
                  .getPublicUrl(img.storage_path);
                
                return {
                  id: img.id,
                  name: img.name,
                  description: img.description || undefined,
                  url: publicUrl,
                  slug: img.slug,
                  createdAt: new Date(img.created_at).getTime(),
                  views: img.views
                };
              }));
            }
          }

          return {
            id: album.id,
            name: album.name,
            description: album.description || undefined,
            slug: album.slug,
            createdAt: new Date(album.created_at).getTime(),
            updatedAt: new Date(album.updated_at).getTime(),
            isPublic: album.is_public,
            views: album.views,
            images: albumImagesData,
            userId: album.user_id
          };
        }));

        setAlbums(fetchedAlbums);
      } catch (err) {
        console.error('Error processing albums:', err);
        toast.error('Error loading albums');
      } finally {
        setLoadingAlbums(false);
      }
    };

    fetchAlbums();
  }, [session]);

  const handleDeleteImage = async (id: string) => {
    try {
      // Find the image to get its storage path
      const imageToDelete = images.find(img => img.id === id);
      if (!imageToDelete) return;

      // Delete from storage
      const storagePath = imageToDelete.url.split('/').pop() || '';
      
      const { error: storageError } = await supabase.storage
        .from('images')
        .remove([storagePath]);

      if (storageError) {
        console.error('Error deleting image from storage:', storageError);
        toast.error('Error deleting image from storage');
        return;
      }

      // Delete from database
      const { error: dbError } = await supabase
        .from('images')
        .delete()
        .eq('id', id);

      if (dbError) {
        console.error('Error deleting image from database:', dbError);
        toast.error('Error deleting image from database');
        return;
      }

      // Update local state
      setImages(images.filter(img => img.id !== id));
      toast.success('Image deleted successfully');
    } catch (error) {
      console.error('Error in delete process:', error);
      toast.error('Failed to delete image');
    }
  };

  const handleDeleteAlbum = async (id: string) => {
    try {
      // Delete album
      const { error } = await supabase
        .from('albums')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting album:', error);
        toast.error('Error deleting album');
        return;
      }

      // Update local state
      setAlbums(albums.filter(album => album.id !== id));
      toast.success('Album deleted successfully');
    } catch (error) {
      console.error('Error in delete process:', error);
      toast.error('Failed to delete album');
    }
  };
  
  // Add a handler for when a new image is uploaded
  const handleImageUpload = (newImage: Image) => {
    setImages([newImage, ...images]);
  };

  const handleAlbumCreate = (newAlbum: Album) => {
    setAlbums([newAlbum, ...albums]);
  };

  const toggleImageSelection = (imageId: string) => {
    if (selectedImages.includes(imageId)) {
      setSelectedImages(selectedImages.filter(id => id !== imageId));
    } else {
      setSelectedImages([...selectedImages, imageId]);
    }
  };

  const addImagesToAlbum = async () => {
    if (!selectedAlbum || selectedImages.length === 0) {
      toast.error('Please select images and an album');
      return;
    }

    try {
      // Create entries in album_images junction table
      const albumImagesData = selectedImages.map(imageId => ({
        album_id: selectedAlbum.id,
        image_id: imageId
      }));

      const { error } = await supabase
        .from('album_images')
        .insert(albumImagesData)
        .select();

      if (error) {
        // Check if it's a unique constraint error
        if (error.code === '23505') {
          toast.warning('Some images are already in this album');
        } else {
          throw error;
        }
      }

      toast.success(`Added ${selectedImages.length} images to "${selectedAlbum.name}"`);
      setSelectedImages([]);
      setAddToAlbumOpen(false);
      setSelectedAlbum(null);
    } catch (error) {
      console.error('Error adding images to album:', error);
      toast.error('Failed to add images to album');
    }
  };

  if (!session?.user) {
    return null;
  }

  return (
    <div className="min-h-screen gradient-bg">
      <Header />
      <div className="container mx-auto pt-24 pb-12 px-4">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold mb-1">Dashboard</h1>
              {isAdmin && (
                <Badge variant="outline" className="bg-primary/20 text-primary border-primary/30">
                  <ShieldCheck className="h-3 w-3 mr-1" /> Admin
                </Badge>
              )}
            </div>
            <p className="text-white/70 text-sm">
              Upload, manage and track your shared images
            </p>
          </div>
        </div>

        <Tabs defaultValue="images" className="space-y-6">
          <TabsList className="bg-black/20 border border-white/10 w-full flex justify-between">
            <TabsTrigger 
              value="images" 
              className="flex-1 data-[state=active]:bg-primary/20 data-[state=active]:text-primary"
            >
              Images
            </TabsTrigger>
            <TabsTrigger 
              value="albums" 
              className="flex-1 data-[state=active]:bg-primary/20 data-[state=active]:text-primary"
            >
              Albums
            </TabsTrigger>
            <TabsTrigger 
              value="stats" 
              className="flex-1 data-[state=active]:bg-primary/20 data-[state=active]:text-primary"
            >
              Stats
            </TabsTrigger>
            <TabsTrigger 
              value="settings" 
              className="flex-1 data-[state=active]:bg-primary/20 data-[state=active]:text-primary"
            >
              Settings
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger 
                value="admin" 
                className="flex-1 data-[state=active]:bg-primary/20 data-[state=active]:text-primary"
              >
                Admin
              </TabsTrigger>
            )}
          </TabsList>
          
          <TabsContent value="images">
            <ImagesTab 
              images={images}
              albums={albums}
              onDeleteImage={handleDeleteImage}
              onImageUpload={handleImageUpload}
              loading={loadingImages}
              selectedImages={selectedImages}
              onToggleSelect={toggleImageSelection}
              setSelectedAlbum={setSelectedAlbum}
              selectedAlbum={selectedAlbum}
              onAddImagesToAlbum={addImagesToAlbum}
              setAddToAlbumOpen={setAddToAlbumOpen}
              addToAlbumOpen={addToAlbumOpen}
            />
          </TabsContent>

          <TabsContent value="albums">
            <AlbumsTab 
              albums={albums}
              loading={loadingAlbums}
              onDeleteAlbum={handleDeleteAlbum}
              onAlbumCreate={handleAlbumCreate}
              onImageUpload={handleImageUpload}
            />
          </TabsContent>
          
          <TabsContent value="stats">
            <Stats />
          </TabsContent>

          <TabsContent value="settings">
            <SiteSettings />
          </TabsContent>

          {isAdmin && (
            <TabsContent value="admin">
              <AdminPanel />
            </TabsContent>
          )}
        </Tabs>
      </div>
      
      {/* Admin floating chat button and UI */}
      {isAdmin && (
        <>
          <FloatingButton 
            onClick={() => setIsChatOpen(true)} 
            isOpen={isChatOpen} 
          />
          <AdminChat 
            isOpen={isChatOpen} 
            onClose={() => setIsChatOpen(false)} 
          />
        </>
      )}
    </div>
  );
};

export default Dashboard;
